import type { Language } from '@seek/ca-myprofile-utils';
import { useLanguage, useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Card,
  Column,
  Columns,
  Drawer,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import React, { useEffect, useState } from 'react';

import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { getLanguagePrefixedPath } from 'src/utils/getLanguagePrefixedPath';

import translations from './.vocab';
import FormContainer from './Form/FormContainer';

export const DeleteAccount = () => {
  const { t } = useTranslations(translations);
  const { trackAccountLink } = useAnalytics();
  const { language } = useLanguage();

  const [openingEventTriggered, setOpeningEventTriggered] = useState(false);

  useEffect(() => {
    if (openingEventTriggered) {
      trackAccountLink({
        eventName: 'settings_section_displayed',
        currentPageSection: 'delete account',
        passwordlessAuthentication: 'full',
      });
    }
  }, [openingEventTriggered, trackAccountLink]);

  const [drawerOpened, setDrawerOpened] = useState(false);

  const toggle = () => {
    const wasClosed = drawerOpened === false;
    if (wasClosed) {
      setOpeningEventTriggered(true);
    }
    setDrawerOpened((currentState) => !currentState);
  };

  return (
    <Box data-testid="delete-account-card">
      <Card rounded>
        <Columns space="small">
          <Column>
            <Text weight="strong">{t('Delete account')}</Text>
          </Column>
          <Column width="content">
            <Button
              id="deleteAccountButton"
              tone="critical"
              variant="transparent"
              bleed
              onClick={toggle}
            >
              {t('Delete')}
            </Button>
          </Column>
        </Columns>
      </Card>

      <Drawer
        id="deleteAccountDrawer"
        title={t('Delete account')}
        open={drawerOpened === true}
        onClose={toggle}
        width="small"
      >
        <Stack space="medium">
          <Text>
            {t(
              "When you delete your account, we'll delete your profile and your job application history (including CVs and cover letters) stored on our platform.",
            )}
          </Text>
          <Text>
            {t(
              "If you've applied for jobs, some employers might still hold your information (including contact details) on their systems. Contact these employers directly to delete this information from their systems. For more information, refer to our Privacy Policy.",
              {
                PrivacyPolicyLink: (children) => (
                  <TextLink
                    href={getLanguagePrefixedPath(
                      language as Language,
                      `/privacy`,
                    )}
                    weight="weak"
                    hitArea="large"
                    target="_blank"
                    onClick={() =>
                      trackAccountLink({
                        currentPageSection: 'delete account',
                        eventName: 'privacy_policy_link_pressed',
                      })
                    }
                  >
                    {children}
                  </TextLink>
                ),
              },
            )}
          </Text>
        </Stack>
        <FormContainer toggle={toggle} />
      </Drawer>
    </Box>
  );
};

export default DeleteAccount;
